"use client";

import { c as _c } from "react/compiler-runtime";
import { useInvalidateBapProfile } from "@/hooks/use-bap-profile";
import { updateBapIdsInBackup } from "@/lib/bap/backup";
import { refreshCloudBackup } from "@/lib/bap/cloud-refresh";
import { createNewIdentity, createSeedIdentity, getSeedAdapter } from "@/lib/bap/core";
import { decryptIdentityBackup } from "@/lib/bap/identity-backup";
import { saveIdentityDraft } from "@/lib/bap/identity-draft";
import { readPendingProfileCreation, resumeProfileCreation } from "@/lib/bap/profile-creation";
import { deriveSeedProfile } from "@/lib/bap/seed-contract";
import { logger } from "@/lib/logger";
import { STORAGE_KEYS } from "@/lib/storage-keys";

/** Validate backup password against the encrypted backup. */
async function validateBackupPassword(password: string): Promise<boolean> {
  const encryptedBackup = localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP);
  if (!encryptedBackup) {
    throw new Error("No encrypted backup found");
  }
  await decryptIdentityBackup(encryptedBackup, password);
  return true;
}

/**
 * Register a BAP ID in the database: organization, owner membership, and
 * profile (with account_pubkey from accountPubkey). Throws on failure — an
 * unregistered identity can't be selected (organization.setActive fails).
 */
async function registerBapIdInDatabase(bapId: string, name: string, accountPubkey: string, counter?: number): Promise<void> {
  const response = await fetch("/api/user/bap-ids", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      bapId,
      name,
      isPrimary: false,
      accountPubkey,
      ...(counter === undefined ? {} : {
        counter
      })
    })
  });
  if (!response.ok) {
    throw new Error(`Failed to register identity (${response.status}): ${await response.text()}`);
  }
}

/**
 * Push the re-encrypted master backup to an existing cloud row. Throws on
 * failure — the new identity only lives in this backup until it reaches the
 * server. Users without a cloud row have not opted in, so that skip is allowed.
 */
async function refreshCloudBackupAfterIdentityCreation(): Promise<void> {
  const result = await refreshCloudBackup();
  if (result.status === "failed") {
    throw new Error(`Failed to sync cloud backup after identity creation: ${result.error}`);
  }
  if (result.status === "skipped" && result.reason !== "no-cloud-backup") {
    throw new Error(`Failed to sync cloud backup after identity creation: ${result.reason}`);
  }
}

/** Re-encrypt the master backup with the newly added identity. */
async function updateEncryptedBackupWithIdentity(password: string): Promise<{
  success: boolean;
  error?: string;
}> {
  const {
    getBAPInstance
  } = await import("@/lib/bap/core");
  const bap = getBAPInstance();
  if (!bap) {
    return {
      success: true
    };
  }
  const updatedIds = bap.exportIds();
  return updateBapIdsInBackup(updatedIds, password);
}

/**
 * Create a NEW account (BAP identity) only — does not create an OAuth client.
 *
 * This is the single shared path for account creation. The combined
 * "account + OAuth client" flow lives only in the OAuth clients wizard, which
 * layers client creation on top of this hook. Creating an account anywhere
 * else (e.g. the identity menu) must stay account-only.
 */
export function useCreateIdentity() {
  const $ = _c(2);
  const invalidateBapProfile = useInvalidateBapProfile();
  let t0;
  if ($[0] !== invalidateBapProfile) {
    const createIdentity = async (name, password) => {
      await validateBackupPassword(password).catch(_temp);
      const seedAdapter = getSeedAdapter();
      let newIdKey;
      if (seedAdapter) {
        const seed = seedAdapter.exportBackup();
        const master = deriveSeedProfile(seed.mnemonic, 0).accountPublicKey;
        if ((seed as {
          inventoryComplete?: false;
        }).inventoryComplete === false) {
          throw new Error("Import your complete backup file before creating another profile.");
        }
        const proposed = readPendingProfileCreation(localStorage, master) ?? deriveSeedProfile(seed.mnemonic, seed.nextProfileIndex);
        newIdKey = await resumeProfileCreation({
          storage: localStorage,
          proposed: {
            master,
            bapId: proposed.bapId,
            index: proposed.index,
            name
          },
          contains: pending => {
            const current = getSeedAdapter()?.exportBackup();
            return !!current && deriveSeedProfile(current.mnemonic, 0).accountPublicKey === master && current.profiles.some(profile => profile.index === pending.index && profile.bapId === pending.bapId);
          },
          allocate: async pending_0 => {
            const current_0 = getSeedAdapter()?.exportBackup();
            if (!current_0 || deriveSeedProfile(current_0.mnemonic, 0).accountPublicKey !== master || current_0.nextProfileIndex !== pending_0.index) {
              throw new Error("Master changed during pending profile creation");
            }
            return (await createSeedIdentity(name, password)).bapId;
          },
          register: async pending_1 => {
            const current_1 = getSeedAdapter()?.exportBackup();
            if (!current_1 || deriveSeedProfile(current_1.mnemonic, 0).accountPublicKey !== master) {
              throw new Error("Master changed during registration");
            }
            const profile_0 = deriveSeedProfile(current_1.mnemonic, pending_1.index);
            await registerBapIdInDatabase(pending_1.bapId, pending_1.name, profile_0.accountPublicKey, pending_1.index);
          },
          cloud: refreshCloudBackupAfterIdentityCreation
        });
      } else {
        const created = createNewIdentity(name);
        if (!created) {
          throw new Error("Only master backups can create new identities.");
        }
        newIdKey = created.bapId;
        const result = await updateEncryptedBackupWithIdentity(password);
        if (!result.success) {
          throw new Error(result.error || "Failed to update backup");
        }
        await registerBapIdInDatabase(newIdKey, name, created.accountPubkey);
        await refreshCloudBackupAfterIdentityCreation();
      }
      const draftResult = await saveIdentityDraft(newIdKey, {
        alternateName: name
      });
      if (!draftResult.success) {
        logger.warn({
          bapId: newIdKey
        }, "Failed to save draft profile for new identity");
      }
      invalidateBapProfile(newIdKey);
      return newIdKey;
    };
    t0 = {
      createIdentity
    };
    $[0] = invalidateBapProfile;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function _temp() {
  throw new Error("Password does not match your encrypted backup");
}